/**
 * Detalle del expediente (CaseDetail) desde `legal_flow_expediente` (sozu-supabase-migrations
 * 20261017233400): un expediente por cuenta, con su etapa y el contacto de agente y compradores.
 * Antes el detalle montaba los cinco listados completos (recibidas, aprobado/firma cliente, firma
 * titular, firmado y archivados) para buscar el folio. Aquí se arma el mismo `LegalRequest` que
 * armaba `enrichLegalFlowCases` (hooks/legalFlowEnrich.ts) con cada listado.
 */
import type { CaseStatus, CompradorDetalle, LegalRequest, Priority, TipoPersona } from '@/types/legal-flow';
import { aArchivoSolicitud } from '@/lib/legal-flow/expedientesRpc';

/** La frase del título de cada etapa, como la ponía cada listado. */
const FRASE_TITULO: Record<string, string> = {
  'Solicitud recibida': 'Solicitud de contrato',
  'En revisión legal': 'Contrato en revisión',
  Aprobado: 'Contrato aprobado',
  'Firma cliente': 'Contrato en firma de cliente',
  'Firma titular': 'Contrato pendiente de validación',
  Firmado: 'Contrato firmado',
  Archivado: 'Contrato firmado',
};

/** Las etapas que salían del listado de solicitudes (la descripción habla del apartado). */
const DE_SOLICITUDES = new Set(['Solicitud recibida', 'En revisión legal']);

/** Folio de la URL (`CC-001209`) → id de la cuenta. null si no es el folio de una propiedad. */
export function idCuentaDeFolio(folio: string | null | undefined): number | null {
  const m = /^CC-(\d+)$/i.exec((folio ?? '').trim());
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

const texto = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v);
  return s === '' ? null : s;
};
/** Instante ISO (UTC); null si no hay fecha válida. */
const instante = (v: unknown): string | null => {
  const s = texto(v);
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
};

/** `LegalRequest` del detalle a partir de la respuesta de `legal_flow_expediente`; null = no existe. */
export function aLegalRequestDetalle(r: any): LegalRequest | null {
  if (!r || typeof r !== 'object' || r.id_cuenta_cobranza == null) return null;
  const etapa = String(r.etapa ?? '') as CaseStatus;
  const folio = String(r.folio ?? '');
  const proyecto = texto(r.proyecto) ?? 'Sin proyecto';
  const unidad = r.numero_propiedad ? `Unidad ${r.numero_propiedad}` : '';

  const compradoresDetalle: CompradorDetalle[] = (Array.isArray(r.compradores) ? r.compradores : []).map((c: any) => ({
    idPersona: Number(c?.id_persona),
    name: texto(c?.nombre) ?? 'Sin nombre',
    tipoPersona: (texto(c?.tipo_persona) ?? 'pf') as TipoPersona,
    rfc: texto(c?.rfc),
    phone: texto(c?.telefono),
    email: texto(c?.email),
    // El lead de la oferta (sin filas en compradores) no trae porcentaje.
    ...(c?.porcentaje != null ? { porcentajeCopropiedad: Number(c.porcentaje) } : {}),
  }));
  const counterparties = compradoresDetalle.map((c) => c.name);

  const agente = texto(r.agente);
  const empresa = texto(r.empresa) ?? undefined;
  const fechaCompra = texto(r.fecha_compra);
  const fechaCreacion = instante(r.fecha_creacion);
  const creado = fechaCompra || fechaCreacion || new Date().toISOString();

  return {
    id: folio,
    title: [proyecto, FRASE_TITULO[etapa] ?? '', unidad].filter(Boolean).join(' - '),
    type: 'Nuevo contrato',
    company: empresa || 'Agente Independiente',
    project: proyecto,
    modelo: texto(r.modelo) ?? undefined,
    property: unidad || undefined,
    requester: agente || '-',
    requesterDept: 'Comercial',
    requesterPhone: texto(r.agente_telefono) ?? undefined,
    requesterEmail: texto(r.email_agente) ?? undefined,
    empresaName: empresa,
    counterparty: counterparties[0] ?? 'Sin comprador registrado',
    counterparties,
    compradoresDetalle,
    titular: texto(r.titular) ?? undefined,
    titularIdPersona: r.id_persona_titular != null ? Number(r.id_persona_titular) : null,
    cuentaCobranza: folio,
    idCuentaCobranza: Number(r.id_cuenta_cobranza),
    agenteVendedor: agente ?? undefined,
    fechaCompra: fechaCompra ?? undefined,
    estimatedValue: Number(r.precio_final ?? 0),
    priority: (texto(r.prioridad) ?? 'Bajo') as Priority,
    description: DE_SOLICITUDES.has(etapa)
      ? `Solicitud automática generada por apartado de ${unidad || 'bien'} (${folio}).`
      : `Cuenta ${folio} con propiedad en estatus Vendido.`,
    // Fecha límite = fecha de compra + 15 días; sin fecha de compra, la de creación.
    dueDate: instante(r.fecha_limite) ?? creado,
    status: etapa,
    createdAt: fechaCreacion || creado,
    updatedAt: fechaCompra || fechaCreacion || creado,
    // Sin la llave, el servidor todavía no sabe archivar (falta 20261022235152): undefined.
    ...('archivo' in r ? { archivo: aArchivoSolicitud(r.archivo) } : {}),
  };
}
